// Core
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useRunSimulationTaskMutation } from "state/simulator/simulatorApiSlice";
import {
  selectUseDsoMeasurements,
  setUseDsoMeasurements,
  selectOpportunityCostsDateRange,
  setOpportunityCostsDateRange,
  selectOpenSessionsDateRange,
  setOpenSessionsDateRange,
  selectDeleteSessionsDateRange,
  setDeleteSessionsDateRange,
} from "state/simulator/simulatorSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";
import { useDeleteSessionsBetweenDatesMutation } from "state/sessions/sessionApiSlice";
import { openSuccessAlert, openNotificationPopup } from "state/application/applicationSlice";

// Utils
import { handleStartTask } from "utils/handlers";
import { extractErrorMessage } from "utils/errors";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

// Components
import { ConfirmDialog, DateRangePopover, Toggle } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

const TINTS = {
  opensessions: "rec-tint-info",
  clearmarket: "rec-tint-green",
  settlement: "rec-tint-teal",
  opportunitycosts: "rec-tint-teal",
};

function SimulationDashboard() {
  const { t } = useTranslation(["simulatorDashboard", "common"]);
  const community = useSelector(selectCurrentCommunity);
  const dispatch = useDispatch();

  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  const [runningId, setRunningId] = useState(null);
  // Real outcome of the polled op so the progress card shows success/failure
  // (not a fake "done") and the specific error message per operation.
  const [result, setResult] = useState(null); // null | "success" | "failure"
  const [errorMsg, setErrorMsg] = useState("");
  const lastTaskState = useRef(null);
  const beginRun = (id) => {
    lastTaskState.current = null;
    setResult(null);
    setErrorMsg("");
    setRunningId(id);
  };

  const openSessionsDateRange = useSelector(selectOpenSessionsDateRange);
  const deleteSessionsDateRange = useSelector(selectDeleteSessionsDateRange);
  const dateRange = useSelector(selectOpportunityCostsDateRange);

  const SETTLEMENT_TASK_TYPE = "DEMO_RUN_SETTLEMENT";
  const CLEAR_MARKET_TASK_TYPE = "DEMO_CLEAR_MARKET";
  const OPEN_SESSIONS_TASK_TYPE = "DEMO_OPEN_SESSIONS";
  const CALCULATE_OPPORTUNITY_COSTS_TASK_TYPE = "CALCULATE_OPPORTUNITY_COSTS";
  const activeSettlementTasks = useSelector(selectActiveTasksByType(SETTLEMENT_TASK_TYPE));
  const activeClearMarketTasks = useSelector(selectActiveTasksByType(CLEAR_MARKET_TASK_TYPE));
  const activeOpenSessionsTasks = useSelector(selectActiveTasksByType(OPEN_SESSIONS_TASK_TYPE));
  const activeOpportunityCostsTasks = useSelector(
    selectActiveTasksByType(CALCULATE_OPPORTUNITY_COSTS_TASK_TYPE)
  );
  const returnActiveTasks = () => {
    if (activeSettlementTasks.length > 0) return activeSettlementTasks;
    if (activeClearMarketTasks.length > 0) return activeClearMarketTasks;
    if (activeOpenSessionsTasks.length > 0) return activeOpenSessionsTasks;
    if (activeOpportunityCostsTasks.length > 0) return activeOpportunityCostsTasks;
    return [];
  };

  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    {
      taskId: returnActiveTasks()[0]?.id,
      communityId: community.id,
      groupTask: returnActiveTasks()[0]?.groupTask ? "true" : "false",
    },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: returnActiveTasks().length === 0,
    }
  );

  const useDsoMeasurements = useSelector(selectUseDsoMeasurements);

  const [runSettlement] = useRunSimulationTaskMutation();
  const lastSettlementTime = localStorage.getItem("lastSettlementTime");
  const handleRunSettlement = () => {
    localStorage.setItem("lastSettlementTime", Date.now().toString());
    handleStartTask(
      runSettlement,
      { communityId: community.id, taskName: "run_settlement", useDsoMeasurements },
      dispatch,
      SETTLEMENT_TASK_TYPE,
      false
    )();
    beginRun("settlement");
  };

  const [runClearMarket] = useRunSimulationTaskMutation();
  const lastClearMarketTime = localStorage.getItem("lastClearMarketTime");
  const handleRunClearMarket = () => {
    localStorage.setItem("lastClearMarketTime", Date.now().toString());
    handleStartTask(
      runClearMarket,
      { communityId: community.id, taskName: "clear_market", useDsoMeasurements },
      dispatch,
      CLEAR_MARKET_TASK_TYPE
    )();
    beginRun("clearmarket");
  };

  const [runOpenSessions] = useRunSimulationTaskMutation();
  const lastOpenSessionsTime = localStorage.getItem("lastOpenSessionsTime");
  const handleOpenSessions = () => {
    const [startDate, endDate] = openSessionsDateRange;
    localStorage.setItem("lastOpenSessionsTime", Date.now().toString());
    handleStartTask(
      runOpenSessions,
      { communityId: community.id, taskName: "open_sessions", startDate, endDate },
      dispatch,
      OPEN_SESSIONS_TASK_TYPE
    )();
    beginRun("opensessions");
  };

  const [runCalculateOpportunityCosts] = useRunSimulationTaskMutation();
  const lastOpportunityCostsTime = localStorage.getItem("lastOpportunityCostsTime");
  const handleCalculateOpportunityCosts = () => {
    const [startDate, endDate] = dateRange;
    localStorage.setItem("lastOpportunityCostsTime", Date.now().toString());
    handleStartTask(
      runCalculateOpportunityCosts,
      { communityId: community.id, taskName: "calculate_opportunity_costs", startDate, endDate },
      dispatch,
      CALCULATE_OPPORTUNITY_COSTS_TASK_TYPE
    )();
    beginRun("opportunitycosts");
  };

  const [deleteSessionsBetweenDates, { isLoading: isDeletingSessions }] =
    useDeleteSessionsBetweenDatesMutation();
  const lastDeleteSessionsTime = localStorage.getItem("lastDeleteSessionsTime");
  const handleDeleteSessions = async () => {
    if (!deleteConfirmed) return;
    const [startDate, endDate] = deleteSessionsDateRange;
    localStorage.setItem("lastDeleteSessionsTime", Date.now().toString());
    try {
      const res = await deleteSessionsBetweenDates({
        communityId: community.id,
        startDate,
        endDate,
      }).unwrap();
      dispatch(openSuccessAlert({ title: `${res.deleted_count} sessions deleted` }));
      setDeleteConfirmed(false);
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: returnActiveTasks(),
    dispatch,
    type: returnActiveTasks()[0]?.type,
  });

  const isLoading = returnActiveTasks().length > 0;

  // Capture the polled task's terminal state so the card reflects the REAL
  // per-operation outcome — success, or failure with its specific error.
  useEffect(() => {
    if (!runningId || !taskStatus) return;
    if (taskStatus.state === "SUCCESS" && lastTaskState.current !== "SUCCESS") {
      lastTaskState.current = "SUCCESS";
      setResult("success");
    } else if (taskStatus.state === "FAILURE" && lastTaskState.current !== "FAILURE") {
      lastTaskState.current = "FAILURE";
      setResult("failure");
      setErrorMsg(extractErrorMessage(taskStatus.error) || taskStatus.message || t("failed"));
    }
  }, [taskStatus, runningId, t]);

  const dismissProgress = () => {
    setRunningId(null);
    setResult(null);
    setErrorMsg("");
    lastTaskState.current = null;
  };

  const ago = (ts) => (ts ? moment(parseInt(ts, 10)).fromNow() : "--");

  const setRange = (setter) => (v) => dispatch(setter([v.start || "", v.end || ""]));
  const asRange = (arr) => ({ start: arr?.[0] || "", end: arr?.[1] || "" });

  const operations = [
    {
      id: "opensessions",
      icon: "schedule",
      title: t("opensessions.button"),
      description: t("opensessions.helptext"),
      onClick: handleOpenSessions,
      timestamp: lastOpenSessionsTime,
      range: asRange(openSessionsDateRange),
      onRange: setRange(setOpenSessionsDateRange),
    },
    {
      id: "clearmarket",
      icon: "storefront",
      title: t("clearmarket.button"),
      description: t("clearmarket.helptext"),
      onClick: handleRunClearMarket,
      timestamp: lastClearMarketTime,
    },
    {
      id: "settlement",
      icon: "receipt_long",
      title: t("settlement.button"),
      description: t("settlement.helptext"),
      onClick: handleRunSettlement,
      timestamp: lastSettlementTime,
    },
    {
      id: "opportunitycosts",
      icon: "account_balance",
      title: t("opportunitycosts.button"),
      description: t("opportunitycosts.helptext"),
      onClick: handleCalculateOpportunityCosts,
      timestamp: lastOpportunityCostsTime,
      range: asRange(dateRange),
      onRange: setRange(setOpportunityCostsDateRange),
    },
    {
      id: "deletesessions",
      icon: "delete_sweep",
      title: t("deletesessions.button"),
      description: t("deletesessions.helptext"),
      onClick: () => setAskDelete(true),
      timestamp: lastDeleteSessionsTime,
      range: asRange(deleteSessionsDateRange),
      onRange: setRange(setDeleteSessionsDateRange),
      destructive: true,
    },
  ];

  const progressOp = runningId ? operations.find((o) => o.id === runningId) : null;
  const showProgress = Boolean(progressOp) && !progressOp.destructive && (isLoading || result);

  let statusChip = null;
  if (progressOp) {
    if (result === "failure") {
      statusChip = (
        <span className="rec-chip rec-chip-red">
          <Icon name="error" fill style={{ fontSize: 14 }} />
          {t("failed")}
        </span>
      );
    } else if (result === "success") {
      statusChip = (
        <span className="rec-chip rec-chip-green">
          <Icon name="check_circle" fill style={{ fontSize: 14 }} />
          {t("done")}
        </span>
      );
    } else {
      statusChip = (
        <span className="rec-chip rec-chip-teal">
          <span className="rec-spin">
            <Icon name="progress_activity" style={{ fontSize: 14 }} />
          </span>
          {t("running")}
        </span>
      );
    }
  }

  return (
    <div className="rec-card overflow-visible">
      <div className="rec-sim-head">
        <div className="rec-sim-head-txt">
          <h2>{t("simulationheadertitle")}</h2>
          <p>{t("simulationheaderdescription")}</p>
        </div>
        <div className="rec-sim-switch">
          <Toggle
            on={useDsoMeasurements}
            onClick={() => dispatch(setUseDsoMeasurements(!useDsoMeasurements))}
            ariaLabel={t("useDsoMeasurements")}
          />
          <button
            type="button"
            className="bg-transparent border-0 p-0 cursor-pointer text-inherit font-[inherit]"
            onClick={() => dispatch(setUseDsoMeasurements(!useDsoMeasurements))}
          >
            {t("useDsoMeasurements")}
          </button>
        </div>
      </div>

      <div className="rec-card-pad">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-rec">
          {operations.map((op) => {
            const busy = op.destructive ? isDeletingSessions : isLoading && runningId === op.id;
            const disabled = op.destructive
              ? !deleteConfirmed || isLoading || isDeletingSessions
              : isLoading;
            return (
              <div key={op.id} className={`rec-op-card ${op.destructive ? "is-danger" : ""}`}>
                <div
                  className={`rec-op-ic ${op.destructive ? "" : TINTS[op.id]}`}
                  style={
                    op.destructive
                      ? { background: "var(--danger-soft)", color: "var(--danger)" }
                      : undefined
                  }
                >
                  <Icon name={op.icon} fill />
                </div>
                <div className="rec-op-txt">
                  <b>{op.title}</b>
                  <p>{op.description}</p>
                  <div className="rec-op-meta">
                    <Icon name="schedule" style={{ fontSize: 15 }} />
                    {`${t("lastran")}: `}
                    <b>{ago(op.timestamp)}</b>
                  </div>

                  {op.range && (
                    <div className="rec-sim-op-aux" style={{ marginLeft: 0 }}>
                      <span className="rec-sim-aux-lbl">{t("common:daterange")}</span>
                      <DateRangePopover value={op.range} onChange={op.onRange} />
                    </div>
                  )}

                  {op.destructive && (
                    <label
                      className="rec-sim-confirm"
                      style={{ marginLeft: 0 }}
                      htmlFor="sim-delete-confirm"
                    >
                      <input
                        id="sim-delete-confirm"
                        type="checkbox"
                        checked={deleteConfirmed}
                        onChange={(e) => setDeleteConfirmed(e.target.checked)}
                      />
                      <span>{t("deletesessions.confirm_destructive")}</span>
                    </label>
                  )}
                </div>
                <button
                  type="button"
                  className={`rec-op-btn ${op.destructive ? "danger" : ""}`}
                  disabled={disabled || busy}
                  onClick={op.onClick}
                >
                  {busy ? (
                    <span className="rec-spin">
                      <Icon name="progress_activity" />
                    </span>
                  ) : (
                    <Icon name={op.destructive ? "delete_forever" : "play_arrow"} fill />
                  )}
                  {op.destructive ? t("deletesessions.button") : t("execute")}
                </button>
              </div>
            );
          })}
        </div>

        {showProgress ? (
          <div className="rec-card rec-card-pad mt-rec" style={{ maxWidth: 760 }}>
            <div className="flex items-center justify-between mb-[18px] gap-3">
              <div className="flex items-center gap-[10px]">
                <div className={`rec-op-ic is-sm ${TINTS[progressOp.id]}`}>
                  <Icon name={progressOp.icon} fill />
                </div>
                <b style={{ fontSize: 15.5, fontWeight: 700, color: "var(--ink)" }}>
                  {progressOp.title}
                </b>
              </div>
              {statusChip}
            </div>

            <div className="rec-progress-track mb-[16px]">
              <div
                className={`rec-progress-fill ${result ? "" : "is-indeterminate"}`}
                style={
                  result
                    ? {
                        width: "100%",
                        background: result === "failure" ? "var(--danger)" : "var(--accent)",
                      }
                    : undefined
                }
              />
            </div>

            {result === "failure" && (
              <p
                style={{
                  fontSize: 13,
                  color: "var(--danger)",
                  margin: "0 0 16px",
                  lineHeight: 1.5,
                }}
              >
                {errorMsg}
              </p>
            )}
            {!result && taskStatus?.message && (
              <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 16px" }}>
                {taskStatus.message}
              </p>
            )}

            <button type="button" className="rec-btn rec-btn-ghost" onClick={dismissProgress}>
              <Icon name="close" />
              {t("close")}
            </button>
          </div>
        ) : (
          <div
            className="rec-card rec-card-pad mt-rec flex items-center gap-[14px]"
            style={{ background: "var(--surface-2)", borderStyle: "dashed" }}
          >
            <Icon name="info" style={{ fontSize: 22, color: "var(--accent)" }} />
            <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
              {t("infoNote")}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={askDelete}
        danger
        icon="delete_sweep"
        title={t("deletesessions.confirm_title")}
        body={t("deletesessions.confirm_body")}
        confirmLabel={t("deletesessions.confirm_button")}
        cancelLabel={t("common:cancel")}
        onCancel={() => setAskDelete(false)}
        onConfirm={() => {
          setAskDelete(false);
          handleDeleteSessions();
        }}
      />
    </div>
  );
}

export default SimulationDashboard;
